import {
	type ColumnDef,
	type ColumnFiltersState,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getSortedRowModel,
	Row,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { useLocalStorage, useSessionStorage } from "@uidotdev/usehooks";
import { type RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { trackTableFilterChanged } from "../../../analytics";
import { fuzzyFilter } from "../DataTable/utils";
import { detectFilterChanges } from "../FilterableDataTable/utils";
import { type DynamicDataTableColumn } from "./types";
import { getInitialColumnSizing } from "./utils/getInitialColumnSizing";
import { getProportionalColumnSizing } from "./utils/getProportionalColumnSizing";
import { reconcileEnforcedColumnFilters } from "./utils/reconcileEnforcedColumnFilters";
import { toTanStackColumnDefs } from "./utils/toTanStackColumnDefs";

const EMPTY_FILTERS: ColumnFiltersState = [];
const EMPTY_SORTING: SortingState = [];

export type UseDynamicDataTableStateParams<TData extends object> = {
	name: string;
	columns: readonly DynamicDataTableColumn<TData>[];
	data?: TData[];
	columnVisibility?: VisibilityState;
	defaultColumnFilters?: ColumnFiltersState;
	/**
	 * Column filters driven by an external control (e.g. a scope toggle). Whenever
	 * this value changes, each listed column's filter is set to the given value, or
	 * cleared when the value is an empty array. Filters the user set on other columns
	 * are preserved, and manual edits to these columns persist until the next change.
	 */
	enforcedColumnFilters?: ColumnFiltersState;
	initialSorting?: SortingState;
	searchString?: string;
};

export type UseDynamicDataTableStateResult<TData extends object> = {
	table: ReturnType<typeof useReactTable<TData>>;
	rows: Row<TData>[];
	columnDefs: ColumnDef<TData, unknown>[];
	rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
	parentRef: RefObject<HTMLDivElement | null>;
	fitColumnWidthsToContainer: () => void;
};

export function useDynamicDataTableState<TData extends object>({
	name,
	columns,
	data,
	columnVisibility,
	defaultColumnFilters,
	enforcedColumnFilters,
	initialSorting,
	searchString,
}: UseDynamicDataTableStateParams<TData>): UseDynamicDataTableStateResult<TData> {
	const tanStackColumnDefs = useMemo(() => toTanStackColumnDefs(columns), [columns]);
	const [dataTableStorage, setDataTableStorage] = useLocalStorage<Record<string, number>>(
		`inrev_dynamic_table_${name}`,
		{},
	);

	useEffect(() => {
		setDataTableStorage((current) => getInitialColumnSizing(tanStackColumnDefs, current));
	}, [tanStackColumnDefs, setDataTableStorage]);

	const [sorting, setSorting] = useSessionStorage<SortingState>(
		`${name}_v2_sorting`,
		initialSorting ?? EMPTY_SORTING,
	);
	const [columnFilters, setColumnFiltersRaw] = useSessionStorage<ColumnFiltersState>(
		`${name}_v2_filters`,
		EMPTY_FILTERS,
	);

	const setColumnFilters = useCallback(
		(updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
			setColumnFiltersRaw((prev) => {
				// useSessionStorage passes `null` to a functional updater when nothing is
				// stored yet; normalize so updaters and change detection always see an array.
				const safePrev = prev ?? EMPTY_FILTERS;
				const next = typeof updater === "function" ? updater(safePrev) : updater;
				for (const change of detectFilterChanges(safePrev, next)) {
					trackTableFilterChanged(name, change.columnId, change.values);
				}
				return next;
			});
		},
		[name, setColumnFiltersRaw],
	);

	const hasAppliedDefault = useRef(false);
	useEffect(() => {
		if (hasAppliedDefault.current || !defaultColumnFilters?.length) return;
		if (columnFilters.length === 0) {
			setColumnFilters(defaultColumnFilters);
		}
		hasAppliedDefault.current = true;
	}, [defaultColumnFilters, setColumnFilters, columnFilters]);

	// Apply externally-enforced filters (e.g. a scope toggle) only when their value
	// changes, so the user can freely edit filters between changes. The last applied
	// key is persisted (same key-space as sorting/filters) so navigating away and back
	// doesn't re-fire the enforced filter and clobber the user's manual edits.
	const [lastAppliedEnforcedKey, setLastAppliedEnforcedKey] = useSessionStorage<string | null>(
		`${name}_v2_enforced_key`,
		null,
	);
	useEffect(() => {
		if (enforcedColumnFilters === undefined) {
			// Forget the last key so a later restore of the same value re-applies.
			if (lastAppliedEnforcedKey !== null) setLastAppliedEnforcedKey(null);
			return;
		}
		const key = JSON.stringify(enforcedColumnFilters);
		if (key === lastAppliedEnforcedKey) return;
		setLastAppliedEnforcedKey(key);
		setColumnFilters((prev) => reconcileEnforcedColumnFilters(prev, enforcedColumnFilters));
	}, [enforcedColumnFilters, lastAppliedEnforcedKey, setLastAppliedEnforcedKey, setColumnFilters]);

	const parentRef = useRef<HTMLDivElement>(null);
	const emptyTableData = useMemo((): TData[] => [], []);

	const table = useReactTable<TData>({
		data: data ?? emptyTableData,
		columns: tanStackColumnDefs,
		defaultColumn: {
			enableResizing: false,
			enableSorting: false,
			enableHiding: false,
		},
		state: {
			sorting,
			columnVisibility,
			columnFilters,
			columnSizing: dataTableStorage,
			...(searchString !== undefined ? { globalFilter: searchString } : {}),
		},
		renderFallbackValue: "--",
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		...(searchString !== undefined ? { globalFilterFn: fuzzyFilter } : {}),
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		onColumnSizingChange: setDataTableStorage,
		autoResetAll: false,
	});

	const { rows } = table.getRowModel();

	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 58,
		overscan: 20,
	});

	const fitColumnWidthsToContainer = useCallback(() => {
		const el = parentRef.current;
		if (!el) {
			return;
		}
		const availableWidth = el.clientWidth;
		if (availableWidth <= 0) {
			return;
		}
		const visibleColumnDefs = table.getVisibleFlatColumns().map((c) => c.columnDef) as ColumnDef<
			TData,
			unknown
		>[];
		if (visibleColumnDefs.length === 0) {
			return;
		}
		const next = getProportionalColumnSizing(visibleColumnDefs, availableWidth);
		setDataTableStorage((prev) => ({ ...prev, ...next }));
	}, [setDataTableStorage, table, parentRef]);

	return {
		table,
		rows,
		columnDefs: tanStackColumnDefs,
		rowVirtualizer,
		parentRef,
		fitColumnWidthsToContainer,
	};
}
